// ÖSSZEGZÉS — a Pénzügy lap első füle: mennyi jön be és mennyi megy ki EBBEN a hónapban, egy képernyőn.
//
// A két oldal két külön végpontról jön (`/revenue-forecast` és `/expenses`), és SZÁNDÉKOSAN nem olvad össze
// egyetlen szerver-oldali számmá: a bevétel-oldal a szolgáltatás-hozzárendelésekből számolt ELŐREJELZÉS, a
// kiadás-oldal pedig KÖNYVELT tény. A kettő különbsége tájékoztató egyenleg, nem eredmény-kimutatás — a lap
// ezt ki is mondja.
//
// DEVIZA: az egyenleg CSAK a HUF-oldalt vonja össze. A bevétel-oldal MNB-árfolyammal már HUF-ra váltott, a
// kiadás-oldal viszont devizánként áll (a kiadásokhoz nincs a bevételével egyező, visszakövethető árfolyam) —
// a nem-HUF kiadás ezért külön, kiírva jelenik meg, nem beleszámítva.
//
// A LEKÉRDEZÉS-KULCS a bevétel-oldalon BÁJTRA azonos a „Bevétel-előrejelzés" fül ALAPÉRTELMEZETT szűrőivel:
// így a két fül EGY cache-bejegyzésen osztozik, és a fül-váltás nem indít új, harminc busz-olvasásos kört.

import type { ExpenseSummary } from '@atlas/contracts';
import { subtractDecimals } from '@atlas/shared';
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { HeroStatCard } from '@/components/shared/hero-stat-card';
import { ALL_FILTER_VALUE as ALL, ListToolbar } from '@/components/shared/list-toolbar';
import { MonthStepper } from '@/components/shared/month-stepper';
import { ListErrorState } from '@/components/shared/list-error-state';
import { PageBody } from '@/components/shared/page-body';
import { type PageNotice } from '@/components/shared/page-notices';
import { PanelCard, PanelCardEmpty } from '@/components/shared/panel-card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { expenseKeys } from '@/features/expenses/api';
import { ExpenseAnalyticsPanels } from '@/features/finance/components/expense-analytics-panels';
import { OverviewPanels } from '@/features/finance/components/overview-panels';
import { summaryRevenue } from '@/features/finance/summary-revenue';
import { useExpenseAnalyticsFilters } from '@/features/finance/use-expense-analytics-filters';
import type { FinanceView, FinanceViewProps } from '@/features/finance/view';
import { revenueForecastKeys } from '@/features/revenue-forecast/api';
import { api } from '@/lib/api';
import { isForbiddenStatus } from '@/lib/error-status';
import { formatMoney } from '@/lib/format-money';

/**
 * Az ÖSSZKÉP-kártya szűrő-állapota: SZÁNDÉKOSAN az ALAPÉRTELMEZÉS (a hónap teljes aktív halmaza).
 *
 * MIÉRT FIX: a „mennyi jött be ebben a hónapban" kérdésre nem adhat választ egy félig beállított szűrő —
 * a szűrők a kimutatás-blokktól hatnak. Mellékhatásként a kártya és a kimutatás alap-nézete UGYANAZT a
 * cache-bejegyzést használja, tehát a lap nem kérdezi meg kétszer ugyanazt.
 */
const DEFAULT_FORECAST_FILTERS = {
  search: '',
  statuses: ['active'],
  billingType: ALL,
  mainCategory: [] as string[],
  subCategory: [] as string[],
  serviceId: ALL,
  planId: ALL,
  clientId: ALL,
  accountId: ALL,
};

/**
 * Az ÖSSZEGZÉS fül nézete. A `TabView` (`content` = az ÁTTEKINTÉS al-nézet) FELÜL egy `expenseDetails` blokkot
 * is ad: az a KIADÁS al-nézet tartalma. A BEVÉTEL al-nézet a kimutatás (`useForecastView`) — a héj választ.
 */
export interface SummaryView extends FinanceView {
  expenseDetails: JSX.Element;
  /** A Kiadás-elemzés eszköztára (hónap + kereső + szűrők). */
  expenseToolbar: JSX.Element;
}

export function useSummaryView({
  enabled,
  month,
  onMonthChange,
  narrowed = false,
  expenseView = false,
}: FinanceViewProps & {
  /**
   * A KIADÁS-ELEMZÉS al-nézet aktív-e: akkor a kiadás-elemzés a SZŰRT halmazon kér (kereső + szűrők, l.
   * `use-expense-analytics-filters.ts`), és az eszköztár a szűrő-sávot adja. Az Áttekintés mindig szűretlen.
   */
  expenseView?: boolean;
  /**
   * Van-e AKTÍV szűkítés a kimutatáson. Az összkép-kártyák SZÁNDÉKOSAN nem szűrnek (lásd
   * `DEFAULT_FORECAST_FILTERS`) — de ezt KI KELL MONDANI: szűréskor a lap alja átíródik, a három szám
   * viszont áll, és magyarázat nélkül ez „beragadt" számnak látszik. (Mérve: a kereső a kimutatást
   * átírja — MRR 69,6 M → 22,6 M —, az összkép-kártya viszont 76,5 M marad. A két szám külön kérdésre
   * felel, csak eddig semmi nem mondta meg, melyikre.)
   */
  narrowed?: boolean;
}): SummaryView {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const forecastKey = revenueForecastKeys.forecast({ month, ...DEFAULT_FORECAST_FILTERS });
  // CSAK az Áttekintés kéri: a Kiadás-elemzésen nem látszik bevétel-szám, és egy lekérés ~30 busz-olvasás
  // („egy al-nézet csak a SAJÁT adatát kéri" — a Kiadás-elemzés korábban erre is várt, skeletonon ülve)
  const forecastQ = api.revenueForecast.useQuery(
    forecastKey,
    { query: { month, status: ['active'] } },
    { enabled: enabled && !expenseView, queryKey: forecastKey, staleTime: 5 * 60 * 1000 },
  );

  // A kiadás-oldalt a lista-végpont adja: az ÖSSZEGZÉST a hónap teljes halmazán számolja, tehát egy üres
  // szűrőjű, EGY soros lekérés is a teljes havi képet hozza (`limit: 1` — a sorokra itt nincs szükség).
  const expenseKey = expenseKeys.summary(month);
  const expenseQ = api.expenses.list.useQuery(
    expenseKey,
    { query: { month, limit: 1 } },
    { enabled, queryKey: expenseKey },
  );

  // A GRAFIKONOK kiadás-oldala: a 12 hónapos trend és a hónap bontásai — egyetlen busz-olvasásból. A Kiadás-
  // elemzésen a szűrt halmazon (a szűretlen eset kulcsa megegyezik az Áttekintésével → közös cache).
  const xa = useExpenseAnalyticsFilters({ enabled: enabled && expenseView });
  const analyticsFilters = expenseView ? xa.query : {};
  const analyticsKey = expenseKeys.analytics(month, analyticsFilters);
  const analyticsQ = api.expenses.analytics.useQuery(
    analyticsKey,
    { query: { month, ...analyticsFilters } },
    { enabled, queryKey: analyticsKey },
  );
  const analytics = analyticsQ.data?.status === 200 ? analyticsQ.data.body : undefined;

  const forecast = forecastQ.data?.status === 200 ? forecastQ.data.body : undefined;
  const summary: ExpenseSummary | undefined =
    expenseQ.data?.status === 200 ? expenseQ.data.body.summary : undefined;

  // lezárt hónapon a TÉNY, nyitotton a TERV (`summary-revenue.ts`) — a kártya, az egyenleg és a haszonkulcs EGY szám
  const { amount: revenue, actual: revenueIsActual } = summaryRevenue(forecast);
  const hufExpense = summary?.totals.find((tot) => tot.currency === 'HUF')?.netAmount ?? '0';
  const otherCurrencies = (summary?.totals ?? []).filter((tot) => tot.currency !== 'HUF');
  const balance = subtractDecimals(revenue, hufExpense);
  // A BETÖLTÉS és a HIBA al-nézetenként: az Áttekintésnek mindhárom lekérés kell, a Kiadás-elemzésnek a két
  // kiadás-oldali. HIBÁNÁL hiba-állapot áll — NEM csendes 0: korábban egy 502 / 429 után a „Bevétel" kártya
  // 0 Ft-ot, az egyenleg a kiadás mínuszát mutatta, jelzés nélkül (review-lelet).
  const needed = expenseView ? [expenseQ, analyticsQ] : [forecastQ, expenseQ, analyticsQ];
  const isLoading = needed.some((q) => q.isLoading);
  const isError =
    !isLoading && needed.some((q) => q.isError || (!!q.data && q.data.status !== 200));
  const isForbidden = needed.some((q) => isForbiddenStatus(q.data?.status ?? q.error?.status));
  const retry = (): void => {
    for (const q of needed) void q.refetch();
  };
  const errorState = <ListErrorState forbidden={isForbidden} onRetry={retry} />;

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2">
      <MonthStepper month={month} onMonthChange={onMonthChange} />
      <Text variant="secondary" as="span" className="text-xs">
        {t(revenueIsActual ? 'finance.summary.noteActual' : 'finance.summary.note')}
      </Text>
    </div>
  );

  // A FÜL HOZZA A SAJÁT ELRENDEZÉSÉT (`TabView`): ez olvasó lap, tehát a `PageBody` térközét kapja — a
  // lista-fül ugyanígy a lista-lapok `px-4 pb-2` dobozát.
  /**
   * Az ÁTTEKINTÉS al-nézet: a három összkép-kártya, alattuk a 12 hónapos bevétel–kiadás grafikon és a hónap
   * legnagyobb bevételi forrásai / kiadási kategóriái. A bevétel RÉSZLETEI (a kimutatás a saját szűrőivel) és
   * a kiadás részletei külön al-nézeten állnak — a lap így egy képernyőn belül olvasható marad.
   */
  const content = (
    <PageBody align="left" tight>
      {isError ? (
        errorState
      ) : isLoading ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-56 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <HeroStatCard
              tone="success"
              icon={<ArrowTrendingUpIcon className="size-5" />}
              title={t('finance.summary.revenue')}
              description={t(
                revenueIsActual
                  ? 'finance.summary.revenueDescActual'
                  : 'finance.summary.revenueDesc',
              )}
              value={formatMoney(revenue, 'HUF', locale)}
            />
            <HeroStatCard
              tone="danger"
              icon={<ArrowTrendingDownIcon className="size-5" />}
              title={t('finance.summary.expense')}
              description={t('finance.summary.expenseDesc')}
              value={formatMoney(hufExpense, 'HUF', locale)}
            />
            <HeroStatCard
              tone="brand"
              icon={<ScaleIcon className="size-5" />}
              title={t('finance.summary.balance')}
              description={t('finance.summary.balanceDesc')}
              value={formatMoney(balance, 'HUF', locale)}
            />
          </div>
          {/* A SZŰRÉS HATÓKÖRE — csak akkor, ha van mit tisztázni (szűkítés nélkül a mondat csak zaj). */}
          {narrowed && (
            <Text variant="secondary" as="p" className="text-xs">
              {t('finance.summary.unfilteredNote')}
            </Text>
          )}
          <OverviewPanels forecast={forecast} analytics={analytics} />
        </div>
      )}
    </PageBody>
  );

  /**
   * A KIADÁS-OLDAL FIGYELMEZTETŐ SÁVJAI — a héj a fül-sáv ALÁ rendereli (`TabView.notices`), nem a lap aljára.
   *
   * MIÉRT SZÁMÍT: mindhárom jelzés arról szól, hogy a FÖNTI kiadás- és egyenleg-szám alulbecsül vagy hiányos.
   * A lap legalján állva pontosan azt a számot nem minősítették, ami miatt a felhasználó a lapra jött —
   * és két képernyőnyi grafikon választotta el őket (user-lelet).
   */
  const notices: PageNotice[] = [];
  if (!isLoading) {
    // A nem-HUF kiadás NEM olvad az egyenlegbe — ezt kimondjuk, nem elhallgatjuk.
    if (otherCurrencies.length > 0) {
      notices.push({
        key: 'foreign',
        tone: 'warning',
        title: (
          <span className="flex flex-wrap items-center gap-2">
            {t('finance.summary.foreignNote')}
            {otherCurrencies.map((row) => (
              <Badge key={row.currency} variant="warning" className="tabular-nums">
                {formatMoney(row.netAmount, row.currency, locale)}
              </Badge>
            ))}
          </span>
        ),
      });
    }
    // A KIADÁS-KÁRTYA ALULBECSÜL, ha van összeg nélküli sor.
    if (summary !== undefined && summary.unknownAmount > 0) {
      notices.push({
        key: 'unknown',
        tone: 'warning',
        title: t('finance.summary.unknownAmount', { count: summary.unknownAmount }),
      });
    }
    // SEMLEGES: a besorolatlan tételek száma állapot-információ, nem hiba.
    if (summary !== undefined && summary.uncategorized > 0) {
      notices.push({
        key: 'uncategorized',
        tone: 'neutral',
        title: t('finance.summary.uncategorized', { count: summary.uncategorized }),
      });
    }
  }

  /**
   * A KIADÁS al-nézet: a grafikonok (kategória-megoszlás, bér / egyéb, 12 hónapos trend, szállítók), alattuk a
   * MEGLÉVŐ kategória-panel — az devizánként is bont, tehát a devizás kiadás itt tételesen látszik.
   */
  const expenseDetails = isError ? (
    <PageBody align="left" tight>
      {errorState}
    </PageBody>
  ) : isLoading ? (
    <PageBody align="left" tight>
      <Skeleton className="h-56 rounded-xl" />
    </PageBody>
  ) : (
    <PageBody align="left" tight className="space-y-4">
      <ExpenseAnalyticsPanels analytics={analytics} />
      {/* a hónap kategória-bontása a SZŰRT halmazon (a lista összegzésének szabályával, l. a BFF `byCategory`-ját) */}
      <ExpenseCategoryPanel rows={analytics?.byCategory ?? []} locale={locale} t={t} />
    </PageBody>
  );

  /** A KIADÁS-ELEMZÉS eszköztára: hónap-léptető + kereső + szűrők (a Bevétel-elemzés sávjának mintájára). */
  const expenseToolbar = (
    <ListToolbar
      leading={<MonthStepper month={month} onMonthChange={onMonthChange} />}
      search={{
        value: xa.searchInput,
        onChange: xa.setSearchInput,
        placeholder: t('common.searchPlaceholder'),
      }}
      filterItems={xa.filterItems}
    />
  );

  return { toolbar, notices, content, expenseDetails, expenseToolbar };
}

/**
 * KIADÁSOK KATEGÓRIÁNKÉNT — a havi költség-bontás, DEVIZÁNKÉNT csoportosítva, arány-sávval.
 *
 * ══ MIÉRT DEVIZÁNKÉNT ═══════════════════════════════════════════════════════════════════════════════════
 * A `byCategory` sorai (kategória × deviza) párok, tehát a BESOROLATLAN kategória ANNYISZOR szerepel, ahány
 * devizában van besorolatlan tétel — a listán így KÉT, betűre azonos „besorolatlan" sor állt egymás alatt,
 * egy HUF-os és egy EUR-os összeggel (user-lelet). A név nem tette megkülönböztethetővé őket, mert a
 * megkülönböztető adat a DEVIZA volt, és az csak az összeg végén, apró betűvel jelent meg.
 *
 * Deviza-szakaszokra bontva a kérdés is helyreáll: „mire ment el a HUF" és „mire ment el az EUR" KÉT külön
 * kérdés — a kettő összege amúgy sem adható össze (a lap egyenlege is csak a HUF-oldalt vonja össze).
 * Egyetlen deviza esetén a szakasz-fejléc elmarad: ott nincs mit megkülönböztetni.
 *
 * ══ MIÉRT ARÁNY-SÁV ═════════════════════════════════════════════════════════════════════════════════════
 * Puszta szám-oszlopból a „mi viszi a pénzt" kérdés fejben-osztást kér. A sáv a szakasz LEGNAGYOBB tételéhez
 * mér (nem az összeghez): így a leghosszabb sáv mindig teli, és a sorok EGYMÁSHOZ olvashatók — ugyanaz az
 * elv, mint a bevétel-oldal `BarList`-jén, hogy a lap két fele egy nyelvet beszéljen.
 */
function ExpenseCategoryPanel({
  rows,
  locale,
  t,
}: {
  rows: ExpenseSummary['byCategory'];
  locale: string;
  t: TFunction;
}): JSX.Element {
  // Deviza-szakaszok, a soroknál MEGŐRZÖTT szerver-sorrenddel (a besorolatlan elöl).
  const currencies = [...new Set(rows.map((r) => r.currency))];

  return (
    <PanelCard title={t('finance.summary.byCategory')}>
      {rows.length === 0 ? (
        <PanelCardEmpty>{t('finance.summary.byCategoryEmpty')}</PanelCardEmpty>
      ) : (
        // A DEVIZA-SZAKASZOK egymás MELLETT, ha van hely: teljes szélességű sorokban a kategória-név és az
        // összeg 1920 px-en másfél méterre került egymástól, a lap alja pedig három üres hasábbal állt.
        <div className="grid gap-x-8 gap-y-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
          {currencies.map((currency) => {
            const group = rows.filter((r) => r.currency === currency);
            // A sáv VISZONYÍTÁSI alapja a szakasz legnagyobb tétele (abszolút értékben — a jóváíró sor negatív).
            const max = Math.max(...group.map((r) => Math.abs(Number(r.netAmount) || 0)), 0);
            return (
              <section key={currency} className="space-y-1.5">
                {/* A szakasz-fejléc CSAK többdevizás hónapon — egyetlen devizánál nincs mit elkülöníteni
                    (ott a rács is egy hasáb, tehát a fejléc csak zaj lenne). */}
                {currencies.length > 1 && (
                  <Text variant="overline" as="div">
                    {currency}
                  </Text>
                )}
                {group.map((row) => {
                  const value = Math.abs(Number(row.netAmount) || 0);
                  const share = max > 0 ? Math.max((value / max) * 100, 1.5) : 0;
                  return (
                    <div key={`${row.categoryId ?? 'none'}|${row.currency}`} className="space-y-1">
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate">
                          {row.categoryName === null
                            ? t('expenses.uncategorized')
                            : row.categoryName.en && locale.toLowerCase().startsWith('en')
                              ? row.categoryName.en
                              : row.categoryName.hu}
                        </span>
                        <span className="flex shrink-0 items-baseline gap-2">
                          <Text variant="secondary" tone="tertiary" as="span" className="text-xs">
                            {t('finance.summary.itemCount', { count: row.count })}
                          </Text>
                          <span className="tabular-nums">
                            {formatMoney(row.netAmount, row.currency, locale)}
                          </span>
                        </span>
                      </div>
                      {/* A sáv `aria-hidden`: az értéket a fenti sor MÁR kimondja, a sáv csak arány-jel. */}
                      <div className="bg-sunken h-1.5 overflow-hidden rounded-full" aria-hidden>
                        <div
                          className="bg-danger-500/70 h-full rounded-full"
                          style={{ width: `${String(share)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}
    </PanelCard>
  );
}
